import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowRightLeft, Archive, CheckCircle2, Plus } from "lucide-react";
import { Modal } from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/Button";
import { cn } from "../../../../utils/cn";
import { formatPhone } from "../../../../layouts/helpdeskLayout/HelpdeskRail";
import {
  getUnofficialWaRetirementPreview,
  retireUnofficialWa,
} from "../../../../api/channelIntegrations";
import QrCodeModal from "./QrCodeModal";

const PURPOSE_LABELS = {
  helpdesk: "Mesa de ayuda",
  notifications: "Notificaciones",
  both: "Mesa de ayuda y notificaciones",
};

const MODE_MIGRATE = "migrate";
const MODE_RETIRE = "retire";

/**
 * Nombre del número para los textos: el teléfono formateado y, si el número
 * tiene un nombre distinto, entre paréntesis.
 */
function numberName(entity) {
  if (!entity) return "";
  const phone = formatPhone(entity.phone);
  const label = entity.label && entity.label !== entity.phone ? entity.label : null;
  if (phone && label) return `${phone} (${label})`;
  return phone || label || "";
}

/**
 * Retiro definitivo de un número de WhatsApp no oficial (bloqueado, dado de
 * baja). El operador elige:
 *  - Migrar a otro número: conversaciones, historial, contactos, tickets y la
 *    configuración de IA pasan al número nuevo.
 *  - Retirar sin reemplazo: sus conversaciones abiertas salen de las colas y
 *    quedan en "Números retirados", donde se pueden cerrar en bloque.
 *
 * El backend agrupa todos los registros del mismo teléfono y es idempotente.
 * Solo supervisor o administrador: el backend responde 403 a cualquier otro rol
 * y el mensaje se muestra tal cual.
 *
 * @param {{isOpen: boolean, integrationId: string|null, onClose: () => void,
 *   onRetired?: (result: object) => void}} props
 */
export default function RetireNumberModal({ isOpen, integrationId, onClose, onRetired }) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [mode, setMode] = useState(null);
  const [successorId, setSuccessorId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);
  // El modal de QR se abre EN LUGAR de este (dos modales a la vez cerrarían
  // juntos con Esc): este se oculta y vuelve al terminar.
  const [connectingNew, setConnectingNew] = useState(false);

  // Depende SOLO del id: con `t` en las dependencias, un traductor que cambie
  // de identidad entre renders re-dispararía la carga en bucle. El texto
  // genérico del error se traduce al pintar ("" = usar el genérico).
  const loadPreview = useCallback(async () => {
    if (!integrationId) return;
    setLoadingPreview(true);
    setPreviewError(null);
    const res = await getUnofficialWaRetirementPreview(integrationId);
    setLoadingPreview(false);
    if (res?.error) {
      setPreviewError(res.error.message || "");
      return;
    }
    setPreview(res);
    // Un número ya retirado sin reemplazo solo puede migrarse.
    if (res.state === "retired") setMode(MODE_MIGRATE);
  }, [integrationId]);

  useEffect(() => {
    if (!isOpen) return;
    setPreview(null);
    setMode(null);
    setSuccessorId(null);
    setSubmitError(null);
    setResult(null);
    loadPreview();
  }, [isOpen, loadPreview]);

  const candidates = preview?.candidates ?? [];
  const successor = candidates.find((c) => c.id === successorId) || null;
  const canSubmit =
    !!preview &&
    preview.state !== "migrated" &&
    !submitting &&
    (mode === MODE_RETIRE || (mode === MODE_MIGRATE && !!successor));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    const res = await retireUnofficialWa(integrationId, {
      mode,
      replacementIntegrationId: successorId,
    });
    setSubmitting(false);
    if (res?.error) {
      setSubmitError(
        res.error.status === 403
          ? t("Solo un supervisor o administrador puede retirar o migrar números.")
          : res.error.message || t("No se pudo retirar el número. Intenta de nuevo.")
      );
      return;
    }
    setResult(res);
    onRetired?.(res);
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const name = numberName(preview);
  const conversations = preview?.conversations ?? { total: 0, open: 0 };

  const footer = result ? (
    <Button size="sm" onClick={handleClose}>
      {t("Listo")}
    </Button>
  ) : (
    <>
      <Button variant="ghost" size="sm" onClick={handleClose} disabled={submitting}>
        {t("Cancelar")}
      </Button>
      {preview && preview.state !== "migrated" && (
        <Button
          variant={mode === MODE_RETIRE ? "danger" : "primary"}
          size="sm"
          onClick={handleSubmit}
          disabled={!canSubmit}
          loading={submitting}
        >
          {mode === MODE_RETIRE
            ? t("Retirar número")
            : successor
            ? t("Migrar a {{numero}}").replace("{{numero}}", numberName(successor))
            : t("Migrar")}
        </Button>
      )}
    </>
  );

  return (
    <>
      <Modal
        open={isOpen && !connectingNew}
        onClose={handleClose}
        size="md"
        title={t("Retirar o migrar número")}
        description={name || undefined}
        footer={footer}
      >
        {loadingPreview && (
          <p className="text-sm text-fg-muted" role="status">
            {t("Cargando…")}
          </p>
        )}

        {previewError !== null && (
          <div role="alert" className="flex items-start gap-2 text-sm text-status-error">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
            <span className="flex-1">
              {previewError || t("No se pudo cargar la información del número.")}
            </span>
            <button type="button" onClick={loadPreview} className="underline font-medium">
              {t("Reintentar")}
            </button>
          </div>
        )}

        {result && <RetirementResult result={result} successor={successor} />}

        {preview && !result && preview.state === "migrated" && (
          <p className="text-sm text-fg">
            {t("Este número ya se migró a otro número. Sus conversaciones están en el número nuevo.")}
          </p>
        )}

        {preview && !result && preview.state !== "migrated" && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-fg-muted">
              {t(
                "Úsalo cuando el número no va a volver (bloqueado o dado de baja). Si solo se desconectó, vuelve a vincularlo con el QR."
              )}
            </p>

            {preview.records > 1 && (
              <p className="text-xs text-fg-subtle">
                {t("Este número tiene {{n}} vinculaciones guardadas; se retiran todas juntas.").replace(
                  "{{n}}",
                  String(preview.records)
                )}
              </p>
            )}

            <fieldset className="flex flex-col gap-2">
              <legend className="sr-only">{t("¿Qué hacemos con el número?")}</legend>
              <ModeOption
                checked={mode === MODE_MIGRATE}
                onSelect={() => setMode(MODE_MIGRATE)}
                icon={ArrowRightLeft}
                title={t("Migrar a otro número")}
                description={t(
                  "Mismos clientes, mismo equipo, mismo historial: todo pasa al número nuevo."
                )}
              />
              {preview.state !== "retired" && (
                <ModeOption
                  checked={mode === MODE_RETIRE}
                  onSelect={() => setMode(MODE_RETIRE)}
                  icon={Archive}
                  title={t("Retirar sin reemplazo")}
                  description={t(
                    "Sus conversaciones abiertas salen de las colas. Las ves en \"Números retirados\" y puedes cerrarlas en bloque."
                  )}
                />
              )}
            </fieldset>

            {mode === MODE_MIGRATE && (
              <SuccessorPicker
                candidates={candidates}
                selectedId={successorId}
                onSelect={setSuccessorId}
                onConnectNew={() => setConnectingNew(true)}
              />
            )}

            {mode && (
              <ul className="flex flex-col gap-1.5 text-sm text-fg list-disc pl-5">
                {mode === MODE_MIGRATE ? (
                  <li>
                    {t("{{total}} conversaciones ({{open}} abiertas) pasarán al número nuevo.")
                      .replace("{{total}}", String(conversations.total))
                      .replace("{{open}}", String(conversations.open))}
                  </li>
                ) : (
                  <li>
                    {t("{{open}} conversaciones abiertas saldrán de las colas de trabajo. El historial no se borra.").replace(
                      "{{open}}",
                      String(conversations.open)
                    )}
                  </li>
                )}
                {mode === MODE_MIGRATE && (
                  <li>
                    {t(
                      "La configuración de IA de este número y los envíos de cobranza y alertas que salían por él pasan al número nuevo."
                    )}
                  </li>
                )}
                {mode === MODE_RETIRE && (
                  <li>
                    {t(
                      "Si un cliente vuelve a escribir por otro número activo, su conversación vuelve sola a la bandeja."
                    )}
                  </li>
                )}
                {preview.has_notification_group && (
                  <li>
                    {t(
                      "Los grupos de WhatsApp no se migran: agrega el número nuevo a los grupos y vuelve a elegir el grupo de avisos."
                    )}
                  </li>
                )}
                {preview.connected && (
                  <li>{t("Este número sigue conectado: retirarlo cierra su sesión de WhatsApp.")}</li>
                )}
                <li className="text-fg-muted">{t("No se puede deshacer desde la aplicación.")}</li>
              </ul>
            )}

            {submitError && (
              <div role="alert" className="flex items-start gap-2 text-sm text-status-error">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
                <span>{submitError}</span>
              </div>
            )}
          </div>
        )}
      </Modal>

      <QrCodeModal
        isOpen={connectingNew}
        toggle={() => setConnectingNew(false)}
        defaultPurpose="helpdesk"
        onConnected={() => {
          // El número recién conectado aparece como candidato al recargar.
          loadPreview();
        }}
      />
    </>
  );
}

function ModeOption({ checked, onSelect, icon: Icon, title, description }) {
  return (
    <label
      className={cn(
        "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
        checked ? "border-brand-500 bg-brand-50/50" : "border-border hover:bg-bg-muted"
      )}
    >
      <input type="radio" name="retire-mode" checked={checked} onChange={onSelect} className="mt-1" />
      <Icon className="w-4 h-4 shrink-0 mt-0.5 text-fg-muted" aria-hidden="true" />
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-fg">{title}</span>
        <span className="block text-xs text-fg-muted">{description}</span>
      </span>
    </label>
  );
}

function SuccessorPicker({ candidates, selectedId, onSelect, onConnectNew }) {
  const { t } = useTranslation();
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-sm font-medium text-fg mb-1">{t("Número nuevo")}</legend>
      {candidates.length === 0 && (
        <p className="text-sm text-fg-muted">
          {t("No hay otro número conectado que pueda reemplazarlo. Conecta uno nuevo.")}
        </p>
      )}
      {candidates.map((candidate) => (
        <label
          key={candidate.id}
          className={cn(
            "flex items-start gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors",
            selectedId === candidate.id ? "border-brand-500 bg-brand-50/50" : "border-border hover:bg-bg-muted"
          )}
        >
          <input
            type="radio"
            name="retire-successor"
            checked={selectedId === candidate.id}
            onChange={() => onSelect(candidate.id)}
            className="mt-1"
          />
          <span className="flex-1 min-w-0">
            <span className="block text-sm text-fg tabular-nums">{numberName(candidate)}</span>
            <span className="block text-xs text-fg-muted">
              {t(PURPOSE_LABELS[candidate.purpose] || PURPOSE_LABELS.both)}
            </span>
            {candidate.loses_notifications && (
              <span className="mt-1 flex items-start gap-1 text-xs text-status-warning">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden="true" />
                {t(
                  "Este número no envía notificaciones: los avisos y la cobranza que salían por el número retirado fallarán hasta que elijas otro número para eso."
                )}
              </span>
            )}
          </span>
        </label>
      ))}
      <button
        type="button"
        onClick={onConnectNew}
        className="self-start inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:underline"
      >
        <Plus className="w-4 h-4" aria-hidden="true" />
        {t("Conectar un número nuevo")}
      </button>
    </fieldset>
  );
}

function RetirementResult({ result, successor }) {
  const { t } = useTranslation();
  const migrated = result.mode === MODE_MIGRATE;
  return (
    <div role="status" className="flex items-start gap-3">
      <CheckCircle2 className="w-5 h-5 shrink-0 text-status-success" aria-hidden="true" />
      <div className="text-sm text-fg">
        {migrated ? (
          <p>
            {t("Número migrado a {{numero}}. {{n}} conversaciones pasaron al número nuevo.")
              .replace("{{numero}}", numberName(successor) || t("el número nuevo"))
              .replace("{{n}}", String(result.conversations_moved ?? 0))}
          </p>
        ) : (
          <p>
            {t("Número retirado. {{n}} conversaciones abiertas quedaron en \"Números retirados\".").replace(
              "{{n}}",
              String(result.open_conversations ?? 0)
            )}
          </p>
        )}
      </div>
    </div>
  );
}
